import {
    UnFavoriteGroupRetryErrorNotification,
    favoriteGroupRetryErrorNotification,
    UnFavoriteGroupErrorNotification,
    favoriteGroupErrorNotification as favoriteGroupErrorNotification_1,
    retryLabelInNotification,
    UnFollowGroupRetryErrorNotification,
    followGroupRetryErrorNotification,
    UnFollowGroupErrorNotification,
    followGroupErrorNotification as followGroupErrorNotification_1,
    leaveGroupSuccessNotification as leaveGroupSuccessNotification_1,
    leaveGroupRetryErrorNotification,
    leaveGroupErrorNotification as leaveGroupErrorNotification_1,
    restoreGroupSuccessNotification as restoreGroupSuccessNotification_1,
    viewLabelInNotification,
    restoreGroupRetryErrorNotification,
    restoreGroupErrorNotification as restoreGroupErrorNotification_1,
    approveMembershipRequestsFailedNotification,
    renewGroupRetryErrorNotification,
    renewGroupErrorNotification as renewGroupErrorNotification_1,
    groupDeepLinkErrorNotification as groupDeepLinkErrorNotification_1,
    softDeletedGroupNotificationForOwner,
    softDeletedGroupNotificationForMember,
} from './notificationBarActions.locstring.json';
import loc, { format } from 'owa-localize';
import type { Dispatch } from 'redux';
import {
    createActionFactoryForFeature,
    Feature,
    showNotification,
    createThunk,
    NotificationType,
    ThunkDispatch,
    UnifiedGroup,
    FavoriteEntity,
    PersonType,
} from '../../../../shared';
import { packageStateKey, HubState } from '../../../state';
import { restoreGroupThunk } from '../../data/groups/unifiedGroupActions';
import { toggleFavoriteGroup } from '../../data/groups/favoriteGroupActions';
import { toggleFollowGroup } from '../../data/groups/followGroupActions';
import { leaveGroupThunk } from '../../data/groups/leaveGroupActions';
import { addFavoritePersona, removeFavoritePersona } from '../../data/favoriteAddRemoveDataActions';
import { getPackageState } from '../../../../appstate';

import { renewGroupAction } from '../../data/groups/renewGroupActions';
import { joinGroupCompleted } from '../../data/groups/joinGroupActions';
import { getCurrentUnifiedGroup } from '../../../selectors/groups/currentUnifiedGroupSelector';

const actionFactory = createActionFactoryForFeature(Feature.UnifiedGroups);
export const notificationBarViewApprovedGroupClickedAction = actionFactory<{
    groupId: string;
    hubPersonaId: string;
}>('NotificationBarViewApprovedGroupClickedAction');

export const notificationBarViewRestoredGroupClickedAction = actionFactory<{
    groupId: string;
    hubPersonaId: string;
}>('NotificationBarViewRestoredGroupClickedAction');

export const notificationBarFavoriteRetryClickedAction = actionFactory<{ hubPersonaId: string }>(
    'NotificationBarFavoriteRetryClickedAction'
);

export const notificationBarUnFavoriteRetryClickedAction = actionFactory<{ hubPersonaId: string }>(
    'NotificationBarUnFavoriteRetryClickedAction'
);

export const notificationBarFollowRetryClickedAction = actionFactory<{ groupId: string }>(
    'NotificationBarFollowRetryClickedAction'
);

export const notificationBarUnFollowRetryClickedAction = actionFactory<{ groupId: string }>(
    'NotificationBarUnFollowRetryClickedAction'
);

export const notificationBarLeaveRetryClickedAction = actionFactory<{ groupId: string }>(
    'NotificationBarLeaveRetryClickedAction'
);

export const notificationBarJoinRetryClickedAction = actionFactory<{ groupId: string }>(
    'NotificationBarJoinRetryClickedAction'
);

export const notificationBarRestoreClickedAction = actionFactory<{
    groupId: string;
    hubPersonaId: string;
}>('NotificationBarRestoreClickedAction');

export const notificationBarRenewClickedAction = actionFactory<{ groupId: string }>(
    'NotificationBarRenewClickedAction'
);

export const favoriteGroupErrorNotification = (dispatch: Dispatch) => (
    groupId: string,
    groupName: string,
    isFavorite: boolean,
    hubPersonaId: string,
    groupSmtp: string,
    isRetry: boolean
) => {
    isRetry
        ? showNotification(
              'favoriteGroupCompletedNotification',
              isFavorite
                  ? format(loc(UnFavoriteGroupRetryErrorNotification), groupName)
                  : format(loc(favoriteGroupRetryErrorNotification), groupName),
              NotificationType.Error,
              undefined,
              undefined
          )
        : showNotification(
              'favoriteGroupCompletedNotification',
              isFavorite
                  ? format(loc(UnFavoriteGroupErrorNotification), groupName)
                  : format(loc(favoriteGroupErrorNotification_1), groupName),
              NotificationType.Error,
              loc(retryLabelInNotification),
              () => {
                  dispatch(
                      isFavorite
                          ? notificationBarUnFavoriteRetryClickedAction({ hubPersonaId })
                          : notificationBarFavoriteRetryClickedAction({ hubPersonaId })
                  );
                  toggleFavoriteGroup(dispatch)(
                      groupId,
                      groupName,
                      isFavorite,
                      hubPersonaId,
                      groupSmtp,
                      true
                  );
              }
          );
};

export const favoriteGroupWithCommonFavoritesAPIErrorNotification = (dispatch: ThunkDispatch) => (
    hubPersonaId: string,
    groupName: string,
    isFavorite: boolean,
    isRetry: boolean,
    favoriteId: string,
    favorite?: FavoriteEntity
) => {
    isRetry
        ? showNotification(
              'favoriteGroupCompletedWithCommonFavoritesAPINotification',
              isFavorite
                  ? format(loc(UnFavoriteGroupRetryErrorNotification), groupName)
                  : format(loc(favoriteGroupRetryErrorNotification), groupName),
              NotificationType.Error,
              undefined,
              undefined
          )
        : showNotification(
              'favoriteGroupCompletedWithCommonFavoritesAPINotification',
              isFavorite
                  ? format(loc(UnFavoriteGroupErrorNotification), groupName)
                  : format(loc(favoriteGroupErrorNotification_1), groupName),
              NotificationType.Error,
              loc(retryLabelInNotification),
              () => {
                  dispatch(
                      isFavorite
                          ? notificationBarUnFavoriteRetryClickedAction({ hubPersonaId })
                          : notificationBarFavoriteRetryClickedAction({ hubPersonaId })
                  );
                  dispatch(
                      isFavorite
                          ? removeFavoritePersona(
                                hubPersonaId,
                                favoriteId,
                                groupName,
                                PersonType.Group,
                                true
                            )
                          : addFavoritePersona(favorite, true)
                  );
              }
          );
};

export const followGroupErrorNotification = (dispatch: Dispatch) => (
    groupSmtp: string,
    groupId: string,
    groupName: string,
    isFollowed: boolean,
    isRetry: boolean,
    hubPersonaId: string
) => {
    isRetry
        ? showNotification(
              'followGroupCompletedNotification',
              isFollowed
                  ? format(loc(UnFollowGroupRetryErrorNotification), groupName)
                  : format(loc(followGroupRetryErrorNotification), groupName),
              NotificationType.Error,
              undefined,
              undefined
          )
        : showNotification(
              'followGroupCompletedNotification',
              isFollowed
                  ? format(loc(UnFollowGroupErrorNotification), groupName)
                  : format(loc(followGroupErrorNotification_1), groupName),
              NotificationType.Error,
              loc(retryLabelInNotification),
              () => {
                  dispatch(
                      isFollowed
                          ? notificationBarUnFollowRetryClickedAction({ groupId })
                          : notificationBarFollowRetryClickedAction({ groupId })
                  );
                  toggleFollowGroup(dispatch)(
                      groupSmtp,
                      groupId,
                      groupName,
                      isFollowed,
                      hubPersonaId,
                      true
                  );
              }
          );
};

export const softDeletedGroupNotification = (dispatch: Dispatch) => (
    isOwner: boolean,
    groupName: string
) => {
    showNotification(
        'softDeletedGroupNotification',
        isOwner
            ? format(loc(softDeletedGroupNotificationForOwner), groupName)
            : format(loc(softDeletedGroupNotificationForMember), groupName),
        NotificationType.Error,
        undefined,
        undefined
    );
};

export const leaveGroupSuccessNotification = (groupId: string, groupName: string) => {
    showNotification(
        'leaveGroupSuccessNotification',
        format(loc(leaveGroupSuccessNotification_1), groupName),
        NotificationType.Success,
        undefined,
        undefined
    );
};

export const leaveGroupErrorNotification = (dispatch: ThunkDispatch) => (
    groupId: string,
    groupSmtp: string,
    groupName: string,
    isExpiring: boolean,
    membershipRequestsCount: number,
    userEmailAddress: string,
    isOwner: boolean,
    isRetry: boolean,
    hubPersonaId: string
) => {
    isRetry
        ? showNotification(
              'leaveGroupErrorNotification',
              format(loc(leaveGroupRetryErrorNotification), groupName),
              NotificationType.Error,
              undefined,
              undefined
          )
        : showNotification(
              'leaveGroupErrorNotification',
              format(loc(leaveGroupErrorNotification_1), groupName),
              NotificationType.Error,
              loc(retryLabelInNotification),
              () => {
                  dispatch(notificationBarLeaveRetryClickedAction({ groupId }));
                  dispatch(
                      leaveGroupThunk(
                          groupId,
                          groupSmtp,
                          groupName,
                          isExpiring,
                          isOwner,
                          membershipRequestsCount,
                          userEmailAddress,
                          hubPersonaId,
                          true
                      )
                  );
              }
          );
};

export const joinGroupErrorNotification = (dispatch: ThunkDispatch) => (
    group: UnifiedGroup,
    userEmailAddress: string,
    isRetry: boolean
) => {
    isRetry
        ? showNotification(
              'joinGroupErrorNotification',
              //   TODO for string move
              format('Unable to join group "{0}" group. Please try again later', group.displayName),
              NotificationType.Error,
              undefined,
              undefined
          )
        : showNotification(
              'joinGroupErrorNotification',
              //   TODO for string move
              format('Unable to join group "{0}" group', group.displayName),
              NotificationType.Error,
              loc(retryLabelInNotification),
              () => {
                  dispatch(
                      notificationBarJoinRetryClickedAction({
                          groupId: group.externalDirectoryObjectId,
                      })
                  );
                  dispatch(joinGroupCompleted(group, userEmailAddress, true));
              }
          );
};

export const restoreGroupSuccessNotification = (dispatch: Dispatch) => (
    groupId: string,
    groupName: string,
    hubPersonaId: string
) => {
    showNotification(
        'restoreGroupSuccessNotification',
        format(loc(restoreGroupSuccessNotification_1), groupName),
        NotificationType.Success,
        loc(viewLabelInNotification),
        () => dispatch(notificationBarViewRestoredGroupClickedAction({ groupId, hubPersonaId }))
    );
};

export const restoreGroupErrorNotification = (dispatch: ThunkDispatch) => (
    groupId: string,
    groupSmtp: string,
    groupName: string,
    hubPersonaId: string,
    routingKey: string,
    isRetry: boolean
) => {
    isRetry
        ? showNotification(
              'restoreGroupErrorNotification',
              format(loc(restoreGroupRetryErrorNotification), groupName),
              NotificationType.Error,
              undefined,
              undefined
          )
        : showNotification(
              'restoreGroupErrorNotification',
              format(loc(restoreGroupErrorNotification_1), groupName),
              NotificationType.Error,
              loc(retryLabelInNotification),
              () => {
                  dispatch(notificationBarRestoreClickedAction({ groupId, hubPersonaId }));
                  dispatch(
                      restoreGroupThunk(
                          groupId,
                          groupSmtp,
                          groupName,
                          hubPersonaId,
                          routingKey,
                          true
                      )
                  );
              }
          );
};

export const lpcApproveMembershipRequestNotification = (
    hubPersonaId: string,
    totalMembershipRequestsCount: number,
    failedMembershipRequestsCount: number
) =>
    createThunk(
        'LPCApproveMembershipRequestFailedNotification',
        (dispatch: ThunkDispatch, getState: () => {}): void => {
            const packageState = getPackageState<HubState>(getState(), packageStateKey);
            const currentUnifiedGroup = getCurrentUnifiedGroup(
                packageState.appState,
                packageState.packageState
            );

            // check if the callback is out-of-context i.e. user switched to a different group. In-context notifications are already
            // shown in the card
            if (
                currentUnifiedGroup &&
                currentUnifiedGroup.hubPersonaId !== hubPersonaId &&
                failedMembershipRequestsCount > 0
            ) {
                showNotification(
                    'lPCApproveMembershipRequestFailedNotification',
                    format(
                        loc(approveMembershipRequestsFailedNotification),
                        failedMembershipRequestsCount,
                        currentUnifiedGroup.displayName
                    ),
                    NotificationType.Error,
                    loc(viewLabelInNotification),
                    () =>
                        dispatch(
                            notificationBarViewApprovedGroupClickedAction({
                                groupId: currentUnifiedGroup.externalDirectoryObjectId,
                                hubPersonaId: currentUnifiedGroup.hubPersonaId,
                            })
                        )
                );
            }
        }
    );

export const renewGroupErrorNotification = (dispatch: Dispatch) => (
    groupId: string,
    groupSmtp: string,
    groupName: string,
    isRetry: boolean,
    isFromLpc: boolean,
    hubPersonaId: string
) => {
    isRetry
        ? showNotification(
              'renewGroupErrorNotification',
              format(loc(renewGroupRetryErrorNotification), groupName),
              NotificationType.Error,
              undefined,
              undefined
          )
        : showNotification(
              'renewGroupErrorNotification',
              format(loc(renewGroupErrorNotification_1), groupName),
              NotificationType.Error,
              loc(retryLabelInNotification),
              () => {
                  dispatch(notificationBarRenewClickedAction({ groupId }));
                  renewGroupAction(dispatch)(
                      groupId,
                      groupSmtp,
                      groupName,
                      hubPersonaId,
                      true,
                      isFromLpc
                  );
              }
          );
};

export const groupDeepLinkErrorNotification = (dispatch: ThunkDispatch) => () => {
    showNotification(
        'groupDeepLinkErrorNotification',
        loc(groupDeepLinkErrorNotification_1),
        NotificationType.Error,
        undefined,
        undefined
    );
};
export type { Action } from '../../../../shared';
